/**
 * React Query key factory for consistent cache management.
 *
 * Provides hierarchical keys: invalidating a parent key (e.g., categorisations.all)
 * automatically invalidates all child keys (list, reviewQueue, detail).
 */
export const queryKeys = {
  categorisations: {
    all: ['categorisations'] as const,
    list: (params?: Record<string, string>) =>
      [...queryKeys.categorisations.all, 'list', params] as const,
    reviewQueue: () =>
      [...queryKeys.categorisations.all, 'review-queue'] as const,
    publicationQueue: () =>
      [...queryKeys.categorisations.all, 'publication-queue'] as const,
    wachtrij: () => [...queryKeys.categorisations.all, 'wachtrij'] as const,
    verwerkt: (needsReview: boolean) =>
      [...queryKeys.categorisations.all, 'verwerkt', needsReview] as const,
    detail: (id: string) =>
      [...queryKeys.categorisations.all, 'detail', id] as const,
    corrections: (id: string) =>
      [...queryKeys.categorisations.all, 'corrections', id] as const,
  },
  voorstellen: {
    all: ['voorstellen'] as const,
    lijst: (categorisationId: string) =>
      [...queryKeys.voorstellen.all, 'lijst', categorisationId] as const,
    skillStatus: () => [...queryKeys.voorstellen.all, 'skill-status'] as const,
  },
  metrics: {
    all: ['metrics'] as const,
    period: (hours: number) => [...queryKeys.metrics.all, hours] as const,
  },
  clients: {
    all: ['clients'] as const,
    list: (includeInactive?: boolean) =>
      [...queryKeys.clients.all, 'list', includeInactive] as const,
  },
  teamMembers: {
    all: ['team-members'] as const,
    list: (includeInactive?: boolean) =>
      [...queryKeys.teamMembers.all, 'list', includeInactive] as const,
  },
  analytics: {
    all: ['analytics'] as const,
    period: (days: number) => [...queryKeys.analytics.all, days] as const,
  },
  subcategories: {
    all: ['subcategories'] as const,
  },
  settings: {
    all: ['settings'] as const,
  },
  vaultEntities: {
    all: ['vault-entities'] as const,
    list: () => [...queryKeys.vaultEntities.all, 'list'] as const,
  },
  webhookEvents: {
    all: ['webhook-events'] as const,
    list: (params?: Record<string, string>) =>
      [...queryKeys.webhookEvents.all, 'list', params] as const,
    stats: () => [...queryKeys.webhookEvents.all, 'stats'] as const,
    attempts: (meetingId: string) =>
      [...queryKeys.webhookEvents.all, 'attempts', meetingId] as const,
  },
  vaultWriteFailures: {
    all: ['vault-write-failures'] as const,
    list: (params?: Record<string, string>) =>
      [...queryKeys.vaultWriteFailures.all, 'list', params] as const,
    stats: () => [...queryKeys.vaultWriteFailures.all, 'stats'] as const,
  },
  relocationJobs: {
    all: ['relocation-jobs'] as const,
    list: (params?: Record<string, string>) =>
      [...queryKeys.relocationJobs.all, 'list', params] as const,
  },
} as const
